import {useCallback, useEffect, useMemo, useState} from "react";
import axios, {type CancelToken} from "axios";
import useIsMounted from "../../../hooks/use-is-mounted";
import {FormResponseStatuses} from "../../../../packages/biz-form/core/constants/form-response-statuses";
import type {PartFormQualificationDs, PartFormQualificationFormDs} from "../../../../core/services/api/type-declerations/response";
import {fetchPartFormQualification} from "../utils/fetch-part-form-qualification";
import type {PartFormQualificationErrorInfo} from "../utils/parse-part-form-qualification-error";

export interface UsePartFormQualificationOptions {
    partId?: number | string | null;
    customerId?: number | string | null;
    enabled?: boolean;
}

/**
 * Determines whether the part form qualification feature is enabled based on the presence of valid partId and customerId parameters. Both parameters must be non-null and non-empty for the feature to be considered enabled.
 * @param partId
 * @param customerId
 */
export function isPartFormQualificationEnabled(
    partId?: number | string | null,
    customerId?: number | string | null,
): boolean {
    return partId != null && partId !== "" && customerId != null && customerId !== "";
}

/**
 * Filters the given list of part form qualification forms to return only those that are actionable, meaning they either have no response or their response status is not approved.
 * The list is also sorted alphabetically by form title.
 * @param forms
 */
export function getActionablePartFormQualificationForms(
    forms: PartFormQualificationFormDs[] | null | undefined,
): PartFormQualificationFormDs[] {
    const list = [...(forms ?? [])];
    list.sort((a, b) => (a.formTitle ?? "").localeCompare(b.formTitle ?? ""));
    return list.filter(
        (form) => !form.responseId || form.responseStatus !== FormResponseStatuses.Approved,
    );
}

export function usePartFormQualification({
                                             partId,
                                             customerId,
                                             enabled = true,
                                         }: UsePartFormQualificationOptions) {
    const isMounted = useIsMounted();
    const shouldFetch = enabled && isPartFormQualificationEnabled(partId, customerId);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorInfo, setErrorInfo] = useState<PartFormQualificationErrorInfo | null>(null);
    const [qualification, setQualification] = useState<PartFormQualificationDs | null>(null);

    /**
     * Fetches the part form qualification for the given part and customer. If a cancel token is provided, it will be used to cancel the request if the component unmounts or if the parameters change before the request completes.
     */
    const fetchQualification = useCallback(async (cancelToken?: CancelToken) => {
        if (!isPartFormQualificationEnabled(partId, customerId)) {
            setQualification(null);
            setLoading(false);
            setError(false);
            setErrorInfo(null);
            return null;
        }
        setLoading(true);
        setError(false);
        setErrorInfo(null);
        const result = await fetchPartFormQualification(partId!, customerId!, cancelToken);
        if (!isMounted()) {
            return null;
        }
        if (result.aborted) {
            return null;
        }
        if (result.error) {
            setQualification(null);
            setError(true);
            setErrorInfo(result.errorInfo);
        } else {
            setQualification(result.qualification);
            setErrorInfo(null);
        }
        setLoading(false);
        return result.qualification;
    }, [customerId, isMounted, partId]);

    /**
     * Effect to fetch the part form qualification when the partId, customerId, or enabled state changes. It also handles cleanup by canceling the request if the component unmounts or if the parameters change before the request completes.
     */
    useEffect(() => {
        if (!shouldFetch) {
            setQualification(null);
            setLoading(false);
            setError(false);
            setErrorInfo(null);
            return;
        }
        const cancelTokenSource = axios.CancelToken.source();
        void fetchQualification(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [shouldFetch, fetchQualification]);

    /**
     * Refreshes the part form qualification by re-fetching it from the server. This can be used to manually trigger a refresh, for example after the user submits a form. It is memoized to avoid unnecessary re-renders.
     */
    const refresh = useCallback(async () => fetchQualification(), [fetchQualification]);

    const qualified = qualification?.qualified ?? false;
    const forms = useMemo(() => qualification?.forms ?? [], [qualification]);
    const actionableForms = useMemo(() => getActionablePartFormQualificationForms(forms), [forms]);

    return {
        loading,
        error,
        errorInfo,
        errorMessage: errorInfo?.message ?? null,
        isCustomerEmailError: errorInfo?.isCustomerEmailError ?? false,
        qualified,
        qualification,
        forms,
        actionableForms,
        refresh,
        fetchQualification,
    };
}
